import { StrictMode, useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { retrieveLaunchParams, retrieveRawInitData, retrieveRawLaunchParams } from '@tma.js/sdk-react'
import { init } from './init'
import { initializeAuth } from './lib/auth'
import { initAnalytics } from './lib/analytics'
import { SettingsProvider } from './lib/settings'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

const root = createRoot(document.getElementById('root')!)

function TelegramRequiredMessage() {
  return (
    <div className="min-h-screen bg-telegram-bg flex items-center justify-center p-4">
      <div className="bg-telegram-secondary rounded-2xl p-6 max-w-sm text-center shadow-lg">
        <div className="text-6xl mb-4">📱</div>
        <h1 className="text-xl font-semibold text-telegram-text mb-2">
          Откройте в Telegram
        </h1>
        <p className="text-telegram-hint mb-4">
          Это приложение работает только внутри Telegram как Mini App.
        </p>
        <a
          href="https://t.me/Kodeksy_RF_bot"
          className="inline-block bg-telegram-button text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
        >
          Открыть в Telegram
        </a>
      </div>
    </div>
  );
}

function AppWrapper() {
  const [isReady, setIsReady] = useState(false);
  const [isTelegram, setIsTelegram] = useState(true);

  useEffect(() => {
    async function bootstrap() {
      try {
        // Get launch params from Telegram (may throw outside Telegram)
        const launchParams = retrieveLaunchParams();
        const debug = import.meta.env.DEV;

        // Initialize Telegram Mini App SDK
        await init({ debug });

        // Initialize auth using initDataRaw
        const rawInitData = retrieveRawInitData();
        const rawLaunchParams = retrieveRawLaunchParams();
        const tgWebAppData = rawLaunchParams
          ? new URLSearchParams(rawLaunchParams.replace(/^[?#]/, '')).get('tgWebAppData')
          : undefined;
        const windowInitData = window.Telegram?.WebApp?.initData;
        const initDataRaw = rawInitData || tgWebAppData || windowInitData;

        console.info('[Bootstrap] initDataRaw sources', {
          rawInitData: !!rawInitData,
          rawLaunchParams: !!rawLaunchParams,
          tgWebAppData: !!tgWebAppData,
          windowInitData: !!windowInitData,
          tgWebAppDataObj: !!launchParams.tgWebAppData,
        });
        console.info('[Bootstrap] initDataRaw present:', !!initDataRaw, 'length:', typeof initDataRaw === 'string' ? initDataRaw.length : 0);
        
        if (initDataRaw && typeof initDataRaw === 'string') {
          const authSuccess = await initializeAuth(initDataRaw);
          console.info('[Bootstrap] Auth result:', authSuccess);
        } else {
          console.warn('[Bootstrap] No initDataRaw available');
        }

        initAnalytics();
        setIsTelegram(true);
        setIsReady(true);
      } catch (error) {
        console.error('[Bootstrap] Failed to initialize:', error);
        // Check if we're in development mode or have a valid session
        if (import.meta.env.DEV) {
          // In dev mode, allow access without Telegram
          setIsTelegram(true);
          setIsReady(true);
        } else {
          // In production, show Telegram required message
          setIsTelegram(false);
          setIsReady(true);
        }
      }
    }

    bootstrap();
  }, []);

  if (!isReady) {
    return (
      <div className="min-h-screen bg-telegram-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-telegram-button" />
      </div>
    );
  }

  if (!isTelegram) {
    return <TelegramRequiredMessage />;
  }

  return <SettingsProvider><App /></SettingsProvider>;
}

root.render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppWrapper />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
)
